/**
 * stuburt-mini-cart.js — a small branded "added to cart" confirmation that
 * expands from the header cart icon, replacing Horizon's auto-opening full-height
 * cart drawer as the add-to-cart feedback ON DESKTOP ONLY. Below 750px an add
 * opens the cart sheet itself instead (see #onCartUpdate); the panel is still the
 * fallback there for a failed add, and on pages with no drawer.
 *
 * Markup: snippets/stuburt-mini-cart.liquid (rendered inside <header-actions>).
 * Listens for the `cart:update` ADD event (source 'product-form-component' — the
 * same signal Horizon's cart-icon uses), fired by both the product-card quick-pick
 * and Horizon's PDP product form. On add it suppresses the drawer, pins a panel
 * under the cart icon, and fills the item row from /cart.js.
 *
 * Non-negotiables: Custom Element (no framework), full teardown in
 * disconnectedCallback (the editor re-renders the header aggressively),
 * prefers-reduced-motion handled in CSS. No client-side money — count is a plain
 * integer; titles/images/variant text are product data, not money/dates.
 */

const ICON_SELECTOR = '.header-actions__cart-icon';
// Desktop holds longer: the panel is a small corner card that costs nothing to
// leave up while you read the item row and reach for View cart / Checkout. On
// mobile it's a near-full-width drop-down sitting over the content, so it clears
// at the shorter interval. Desktop also gets the hover/focus pause below.
const DISMISS_MS = 4000;
const DISMISS_MS_DESKTOP = 7000;
// Same breakpoint the panel's own layout and CSS use.
const MOBILE_QUERY = '(max-width: 749px)';

class StuburtMiniCart extends HTMLElement {
  #abort = null;
  #timer = 0;
  #open = false;
  #reduce = false;
  #shownAt = 0;
  #closing = false;

  connectedCallback() {
    // The site header has `contain: layout`, which makes it the containing block
    // (and stacking context) for position:fixed descendants — so a mini-cart left
    // inside the header is NOT fixed to the viewport: it gets trapped in the
    // sticky/animated header and flashes then clips away. Re-home it to <body> so
    // its fixed positioning is truly viewport-relative. Moving fires a
    // disconnect+reconnect; the real setup happens once it's a child of <body>.
    if (this.parentElement !== document.body) {
      const existing = Array.from(document.body.children).find(
        (el) => el.tagName === 'STUBURT-MINI-CART' && el !== this
      );
      // A body-level instance already runs (the editor re-renders the header
      // repeatedly) — drop this duplicate instead of stacking listeners.
      if (existing) {
        this.remove();
        return;
      }
      document.body.appendChild(this);
      return;
    }

    this.panel = this.querySelector('[data-mc-panel]');
    this.confirmEl = this.querySelector('[data-mc-confirm]');
    this.itemRow = this.querySelector('[data-mc-item]');
    this.thumb = this.querySelector('[data-mc-thumb]');
    this.titleEl = this.querySelector('[data-mc-title]');
    this.variantEl = this.querySelector('[data-mc-variant]');
    this.countEl = this.querySelector('[data-mc-count]');
    this.actionsEl = this.querySelector('[data-mc-actions]');
    this.viewCartEl = this.querySelector('[data-mc-view-cart]');
    if (!this.panel) return;

    this.#reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    // Strip the attribute that makes cart-drawer.js call showDialog() on every add,
    // so opening is decided here and in one place: panel on desktop, sheet on
    // mobile (#onCartUpdate calls drawer.open() itself). Degrades safely: if this
    // never runs, you just get Horizon's default drawer back at every width.
    document.querySelector('cart-drawer-component')?.removeAttribute('auto-open');

    document.addEventListener('cart:update', this.#onCartUpdate, { signal });
    // `click` (not pointerdown): the opening Add click is stopPropagation'd by the
    // quick-pick, and PDP add-clicks fire before the async panel opens — so the
    // interaction that opens the panel can never be the one that closes it.
    document.addEventListener('click', this.#onDocClick, { signal });
    document.addEventListener('keydown', this.#onKeydown, { signal });
    window.addEventListener('resize', this.#reposition, { signal, passive: true });
    window.addEventListener('scroll', this.#reposition, { signal, passive: true });
    this.viewCartEl?.addEventListener('click', this.#onViewCart, { signal });
    this.addEventListener('pointerenter', this.#pause, { signal });
    this.addEventListener('pointerleave', this.#resume, { signal });
    this.addEventListener('focusin', this.#pause, { signal });
    this.addEventListener('focusout', this.#resume, { signal });
  }

  disconnectedCallback() {
    window.clearTimeout(this.#timer);
    this.#abort?.abort();
    this.#abort = null;
    this.#open = false;
  }

  #onCartUpdate = (event) => {
    const data = event?.detail?.data ?? {};
    // `quiet` adds (e.g. the free-shipping top-up grid inside the cart) shouldn't
    // pop a confirmation over the cart the customer is already looking at.
    if (data.quiet) return;
    // Only genuine adds — not quantity changes (which share the cart:update name).
    if (data.source !== 'product-form-component') return;

    // MOBILE (2026-08-18): the panel is replaced by the cart sheet itself. On a
    // phone the drop-down covers the content it's confirming, and the next tap is
    // almost always "show me the cart" — so open the real thing and skip the
    // intermediate step. Desktop keeps the corner card, where it costs nothing.
    // A failed add still uses the panel: opening the cart wouldn't explain the error.
    if (!data.didError && window.matchMedia(MOBILE_QUERY).matches) {
      const drawer = document.querySelector('cart-drawer-component');
      if (drawer && typeof drawer.open === 'function') {
        // Same event stock Horizon's auto-open listens to, so the drawer's sections
        // are as fresh here as they'd be with the attribute left on.
        if (this.#open) this.#hide();
        drawer.open();
        return;
      }
      // No drawer on the page (the cart template omits it) — fall through to the panel.
    }

    let message;
    if (data.didError) {
      message = "Couldn't add that — try again";
      if (this.itemRow) this.itemRow.hidden = true;
      if (this.countEl) this.countEl.textContent = '';
      if (this.actionsEl) this.actionsEl.hidden = true;
    } else {
      message = 'Added to your cart';
      if (this.actionsEl) this.actionsEl.hidden = false;
      const p = data.product;
      if (p && p.image) {
        // Fill the item instantly from the card image (already cached) so the
        // previous product's thumbnail never lingers; /cart.js only updates count.
        this.#fillItem({ image: p.image, title: p.title, variant: p.variant });
        this.#hydrate(data.variantId, false);
      } else {
        // No image in the event (e.g. PDP add): hide the stale item until /cart.js
        // resolves the new one, so the previous product never shows.
        if (this.itemRow) this.itemRow.hidden = true;
        this.#hydrate(data.variantId, true);
      }
    }
    this.#show(message);
  };

  #show(message) {
    window.clearTimeout(this.#timer);
    // Cancel any in-flight close so its transitionend/fallback can't hide us.
    this.#closing = false;
    this.hidden = false;
    this.#reposition();
    // Force reflow so the transition runs from the hidden state.
    void this.panel.offsetWidth;
    this.classList.remove('is-closing');
    this.classList.add('is-open');
    this.#open = true;
    this.#shownAt = Date.now();
    // Announce only AFTER the live region is visible (in the a11y tree), so the
    // text mutation is perceived — setting it while hidden would not announce.
    window.requestAnimationFrame(() => {
      if (this.confirmEl) this.confirmEl.textContent = message;
    });
    this.#arm();
  }

  #arm() {
    window.clearTimeout(this.#timer);
    // Read per-open, not cached at connect, so a rotate or resize between adds
    // picks up the right interval.
    const ms = window.matchMedia(MOBILE_QUERY).matches ? DISMISS_MS : DISMISS_MS_DESKTOP;
    this.#timer = window.setTimeout(() => this.#hide(), ms);
  }

  // "View cart" opens the cart panel in place instead of navigating — reaching
  // the basket should never cost a page load, at any width. Originally gated to
  // mobile, back when desktop was still a page; that gate is gone now the drawer
  // is the cart everywhere.
  // Still falls through to the href when no panel exists — on /cart itself, or
  // without JS — so it can never be a dead end.
  #onViewCart = (event) => {
    const drawer = document.querySelector('cart-drawer-component');
    if (!drawer || typeof drawer.open !== 'function') return;

    event.preventDefault();
    this.#hide();
    drawer.open();
  };

  #pause = () => {
    if (this.#open) window.clearTimeout(this.#timer);
  };

  #resume = () => {
    if (this.#open) this.#arm();
  };

  #hide() {
    if (!this.#open) return;
    window.clearTimeout(this.#timer);
    this.#open = false;
    this.#closing = true;
    this.classList.remove('is-open');
    this.classList.add('is-closing');

    const done = () => {
      // If a new add re-opened the panel mid-close, #show cleared #closing — bail.
      if (!this.#closing) return;
      this.#closing = false;
      this.hidden = true;
      this.classList.remove('is-closing');
    };
    if (this.#reduce) {
      done();
      return;
    }
    const onEnd = (event) => {
      if (event.target !== this.panel || event.propertyName !== 'opacity') return;
      done();
    };
    this.panel.addEventListener('transitionend', onEnd, { once: true, signal: this.#abort?.signal });
    // Fallback in case transitionend doesn't fire.
    window.setTimeout(done, 320);
  }

  /** Pin the panel under the cart icon (fixed; no DOM positioning-context dependency). */
  #reposition = () => {
    if (this.hidden) return;
    const icon = document.querySelector(ICON_SELECTOR);
    if (!icon) return;
    const r = icon.getBoundingClientRect();
    const top = Math.round(r.bottom + 8);
    if (window.matchMedia(MOBILE_QUERY).matches) {
      this.style.left = '0.75rem';
      this.style.right = '0.75rem';
      this.style.top = `${top}px`;
    } else {
      this.style.left = 'auto';
      this.style.right = `${Math.max(8, Math.round(window.innerWidth - r.right))}px`;
      this.style.top = `${top}px`;
    }
  };

  #onDocClick = (event) => {
    if (!this.#open) return;
    // Belt-and-braces: ignore any click in the first moment after opening, so the
    // interaction that triggered the add can never immediately dismiss the panel.
    if (Date.now() - this.#shownAt < 300) return;
    // A click outside the panel dismisses. Clicks inside (View cart / Checkout)
    // are left alone so their links navigate.
    if (this.contains(event.target)) return;
    this.#hide();
  };

  #onKeydown = (event) => {
    if (!this.#open || event.key !== 'Escape') return;
    this.#hide();
    document.querySelector(ICON_SELECTOR)?.closest('a, button')?.focus();
  };

  async #hydrate(variantId, needItem) {
    try {
      // Abort on disconnect so a late response never writes to detached DOM.
      const res = await fetch('/cart.js', {
        headers: { Accept: 'application/json' },
        signal: this.#abort?.signal,
      });
      if (!res.ok) return;
      const cart = await res.json();
      if (this.countEl) {
        const n = cart.item_count;
        // Strings come from Liquid (data-count-*) — no UI text baked into JS.
        const one = this.countEl.dataset.countOne || '';
        const other = this.countEl.dataset.countOther || '';
        this.countEl.textContent = n === 1 ? one : other.replace('[count]', n);
      }
      if (!needItem) return;
      let item = null;
      if (variantId) item = cart.items.find((i) => String(i.id) === String(variantId));
      if (!item && cart.items.length) item = cart.items[cart.items.length - 1];
      if (!item) return;
      let variant = '';
      if (item.variant_title && item.variant_title !== 'Default Title') {
        const vals = (item.options_with_values || []).map((o) => o.value).filter(Boolean);
        variant = vals.length ? vals.join(' · ') : String(item.variant_title).replace(/\s*\/\s*/g, ' · ');
      }
      // /cart.js images are full-size — request a small thumbnail width.
      const raw = item.featured_image?.url || item.image || '';
      let image = raw;
      if (raw) {
        try {
          const u = new URL(raw, window.location.origin);
          u.searchParams.set('width', '104');
          image = u.toString();
        } catch {
          /* keep raw */
        }
      }
      this.#fillItem({ image, title: item.product_title || item.title || '', variant });
    } catch {
      /* Item row stays hidden; the confirmation + actions still show. */
    }
  }

  /** @param {{image?: string, title?: string, variant?: string}} item */
  #fillItem({ image, title, variant }) {
    if (this.thumb) {
      if (image) {
        // Use the URL as-is — the picker passes the card's already-loaded image,
        // so this is a cache hit and shows with no extra fetch.
        // Hide until the NEW image is ready (light placeholder shows through), so
        // the previously-added product's thumbnail never lingers.
        this.thumb.hidden = true;
        this.thumb.onload = () => { this.thumb.hidden = false; };
        this.thumb.onerror = () => { this.thumb.hidden = true; };
        this.thumb.removeAttribute('srcset');
        this.thumb.src = image;
        // Already cached → onload may not fire; reveal straight away.
        if (this.thumb.complete && this.thumb.naturalWidth > 0) this.thumb.hidden = false;
      } else {
        this.thumb.hidden = true;
        this.thumb.removeAttribute('src');
        this.thumb.removeAttribute('srcset');
      }
    }
    if (this.titleEl) this.titleEl.textContent = title || '';
    if (this.variantEl) this.variantEl.textContent = variant || '';
    if (this.itemRow) this.itemRow.hidden = false;
  }
}

if (!customElements.get('stuburt-mini-cart')) {
  customElements.define('stuburt-mini-cart', StuburtMiniCart);
}
